import IdeaProperty from "@/types/IdeaProperty";
import {formulaName} from "@/utils/utils";
import {travelsAsList} from "@/utils/propertyDefinition";
import {isMediaType} from "@/utils/mediaTypes";

/**
 * O rollup sem escrever fórmula: da relação, do campo dos relacionados e da agregação, a fórmula
 * e o tipo que ela pede. Puro, para poder ser conferido fora da tela contra a checagem do servidor
 * — o `RollupBuilder` só desenha as escolhas.
 *
 * **É atalho de autoria, e não outro motor**: o resultado é uma fórmula comum
 * (`itens.map(i, i.preco).sum()`), que se edita depois como qualquer outra.
 */

export type Aggregation = 'count' | 'sum' | 'avg' | 'median' | 'min' | 'max' | 'all'

/** O atributo que o construtor monta: a fórmula e o tipo que ela pede. */
export type Rollup = {
    formula: string
    type: string
    subtype?: string
    multiple: boolean
    domain?: string
}

/**
 * Os campos dos relacionados que se podem levar. Mídia nunca — é arquivo, não valor. Numa lista,
 * também não os múltiplos: o `map` daria uma lista de listas, que nenhum atributo guarda.
 */
export function rollupFields(relationship: IdeaProperty, target: IdeaProperty[]): IdeaProperty[] {
    const list = travelsAsList(relationship)
    return target.filter(property => !property.relationship && !isMediaType(property) && !(list && property.multiple))
}

/** Contar sempre; somar e médias só com número; extremos com número e data; todos com qualquer campo. */
export function rollupAggregations(field?: IdeaProperty): Aggregation[] {
    const type = field ? field.type : undefined
    const available: Aggregation[] = ['count']
    if (type === 'number') available.push('sum', 'avg', 'median', 'min', 'max')
    if (type === 'date') available.push('min', 'max')
    if (field) available.push('all')
    return available
}

/** A agregação que mais se usa com o campo escolhido: somar número, mostrar o resto, contar sem campo. */
export function suggestedAggregation(field?: IdeaProperty): Aggregation {
    return !field ? 'count' : field.type === 'number' ? 'sum' : 'all'
}

/** O tipo do campo dos relacionados, que o atributo calculado herda — com o domínio na seleção. */
function typeOf(field: IdeaProperty): {type: string, subtype?: string, domain?: string} {
    const fallback = field.type === 'number' ? 'decimal' : field.type === 'text' ? 'simple' : undefined
    return {
        type: field.type || 'text',
        subtype: field.subtype || fallback,
        domain: field.type === 'choice' ? field.domain : undefined
    }
}

/**
 * O atributo montado, ou `undefined` enquanto falta escolher. Relação para um item só não agrega:
 * é o caminho (`cliente.cidade`), e o atributo herda o campo inteiro, múltiplo inclusive.
 */
export function rollupOf(relationship: IdeaProperty, field: IdeaProperty | undefined,
                         aggregation: Aggregation | null | undefined): Rollup | undefined {
    const path = formulaName(relationship.alias)
    if (!travelsAsList(relationship)) {
        if (!field) return undefined
        return {formula: `${path}.${formulaName(field.alias)}`, ...typeOf(field), multiple: !!field.multiple}
    }
    if (aggregation === 'count') return {formula: `${path}.size()`, type: 'number', subtype: 'integer', multiple: false}
    if (!field || !aggregation) return undefined
    //A variável do item é a inicial da relação: `itens.map(i, i.preco)`
    const item = path.charAt(0)
    const mapped = `${path}.map(${item}, ${item}.${formulaName(field.alias)})`
    if (aggregation === 'all') return {formula: mapped, ...typeOf(field), multiple: true}
    const decimal = aggregation === 'avg' || aggregation === 'median'
    const inherited = typeOf(field)
    return {formula: `${mapped}.${aggregation}()`, ...inherited, subtype: decimal ? 'decimal' : inherited.subtype, multiple: false}
}
